import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantContext = createContext(null);

export function useTenant() {
  return useContext(TenantContext);
}

// Resolve the tenant slug from (in priority order):
//  1) URL query param ?tenant=acme-clinic        (one-off override)
//  2) localStorage `tenantSlug`                   (sticky after login)
//  3) Host subdomain (first DNS label)            (auto-routing)
function resolveInitialSlug() {
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('tenant');
    if (q) { return { slug: q.toLowerCase(), pinned: true }; }
  } catch (_) {}
  const host = window.location.hostname || '';
  // Only treat the first DNS label as a tenant slug when the host has
  // *at least* 3 parts (e.g., kbcenter.app.example.com → kbcenter).
  // A bare 2-part custom domain like `eclinix360.com` has no subdomain
  // — the first label IS the brand itself, not a tenant.
  const parts = host.split('.');
  if (parts.length >= 3) {
    const label = parts[0].toLowerCase();
    if (!['www', 'app', 'api', 'localhost'].includes(label) && !label.startsWith('customer-')) {
      return { slug: label, pinned: true };
    }
  }
  const stored = (window.localStorage?.getItem('tenantSlug') || '').toLowerCase();
  return { slug: stored, pinned: false };
}

export function TenantProvider({ children }) {
  const initial = resolveInitialSlug();
  const [slug, setSlugState] = useState(initial.slug);
  // `pinned` = the slug came from a URL/subdomain override, so the login UI
  // should hide the tenant picker.
  const [pinned, setPinned] = useState(initial.pinned);
  const [tenant, setTenant] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [resolving, setResolving] = useState(false);
  // Platform configuration (app base domain) fetched once at boot.
  const [platformConfig, setPlatformConfig] = useState({ app_base_domain: '', scheme: 'https' });
  // True once /api/platform/config has resolved (success or failure) — used
  // by the login gate to wait for the platform default before deciding
  // whether the URL is missing its required ?tenant= slug.
  const [platformReady, setPlatformReady] = useState(false);

  // Persist the chosen slug + push the global axios header so every
  // subsequent request is automatically tenant-scoped.
  const setSlug = useCallback((s) => {
    const next = (s || '').toLowerCase();
    setSlugState(next);
    if (next) {
      window.localStorage?.setItem('tenantSlug', next);
      axios.defaults.headers.common['X-Tenant-Slug'] = next;
    } else {
      window.localStorage?.removeItem('tenantSlug');
      delete axios.defaults.headers.common['X-Tenant-Slug'];
    }
  }, []);

  // Initialize header from initial slug + persist pinned slug to storage so
  // it survives reloads (subdomain or ?tenant= URL).
  useEffect(() => {
    if (slug) {
      axios.defaults.headers.common['X-Tenant-Slug'] = slug;
      if (pinned) window.localStorage?.setItem('tenantSlug', slug);
    } else {
      delete axios.defaults.headers.common['X-Tenant-Slug'];
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load available tenants for the login screen picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/tenant/list`);
        if (!cancelled) setAvailableTenants(data || []);
      } catch (_) {
        if (!cancelled) setAvailableTenants([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load platform config (base domain + default tenant) once at boot. The
  // `default_tenant_slug` is what the apex domain (e.g. eclinix360.com)
  // resolves to when no subdomain / ?tenant= override is present — without
  // it the SPA would loop forever on /api/tenant/resolve 404s.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/platform/config`);
        if (cancelled) return;
        const cfg = data || { app_base_domain: '', scheme: 'https' };
        setPlatformConfig(cfg);
        // If the SPA booted without any tenant slug AND the platform admin
        // has configured a default, adopt it so subsequent resolve/auth
        // calls succeed.
        if (!slug && cfg.default_tenant_slug) {
          setSlugState(cfg.default_tenant_slug.toLowerCase());
          axios.defaults.headers.common['X-Tenant-Slug'] = cfg.default_tenant_slug.toLowerCase();
        }
      } catch (_) { /* fine — falls back to ?tenant= URLs */ }
      finally { if (!cancelled) setPlatformReady(true); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve metadata for the active slug (name/status/plan).
  useEffect(() => {
    let cancelled = false;
    if (!slug) { setTenant(null); return; }
    setResolving(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/tenant/resolve`, {
          params: { slug },
        });
        if (cancelled) return;
        setTenant(data);
        // Backend uses `fallback: true` to signal that the requested slug
        // didn't exist and a platform-default tenant was returned instead.
        // Switch our local state to that canonical slug so subsequent
        // X-Tenant-Slug headers and links use it — otherwise we'd keep
        // sending the bogus slug on every request.
        if (data?.fallback && data?.slug && data.slug.toLowerCase() !== slug) {
          const canonical = data.slug.toLowerCase();
          setSlugState(canonical);
          axios.defaults.headers.common['X-Tenant-Slug'] = canonical;
          // Don't persist a fallback as a sticky preference — let the
          // platform default keep deciding on each visit.
          try { window.localStorage?.removeItem('tenantSlug'); } catch (_) { /* noop */ }
        }
      } catch (_) {
        if (!cancelled) setTenant(null);
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <TenantContext.Provider
      value={{ slug, tenant, setSlug, availableTenants, resolving, pinned, platformConfig, platformReady }}
    >
      {children}
    </TenantContext.Provider>
  );
}
